/**
 * useDeadlineTick — ONE bounded re-render at the next future deadline
 * (G1-round-3 mounted-freshness correction). Render-time-only freshness
 * reads (Date.now() in the component body) are correct the moment they
 * run but never re-run on their own: a value mounted fresh would sail
 * past its threshold still presented as current until unrelated
 * store/parent activity re-rendered the row. This hook gives such
 * thresholds a clock authority WITHOUT a persistent ticker: pass every
 * upcoming boundary (stale annotations, arrow-hide lifetimes); it arms a
 * single setTimeout for the EARLIEST future one (+ a small slack so the
 * boundary has strictly passed) and bumps a reducer when it fires. The
 * re-render recomputes the deadlines, which re-arms the next boundary —
 * so N boundaries cost N timeouts total, not a subscription. Null/past
 * deadlines are ignored; with none in the future, no timer exists.
 */

import { useEffect, useReducer } from 'react';

const bump = (n: number) => n + 1;

/** Slack past the boundary so threshold comparisons (>) flip for sure. */
const DEADLINE_SLACK_MS = 50;

/** setTimeout's signed-32-bit ceiling (~24.8 days). Longer delays
 *  OVERFLOW and fire immediately in browsers — which would re-arm and
 *  spin. A clamped timer instead fires early, recomputes, and re-arms:
 *  distant deadlines chain in ≤24.8-day hops. */
const MAX_TIMEOUT_MS = 0x7fffffff;

export function useDeadlineTick(deadlines: Array<number | null | undefined>): void {
  const [wakeVersion, tick] = useReducer(bump, 0);
  const nowMs = Date.now();
  let next = Infinity;
  for (const d of deadlines) {
    if (d != null && d > nowMs && d < next) next = d;
  }
  const nextDeadline = Number.isFinite(next) ? next : null;
  useEffect(() => {
    if (nextDeadline == null) return;
    const delayMs = Math.min(
      Math.max(0, nextDeadline - Date.now()) + DEADLINE_SLACK_MS,
      MAX_TIMEOUT_MS,
    );
    const t = setTimeout(tick, delayMs);
    return () => clearTimeout(t);
    // A clamped/early wake or backward wall-clock step can leave the same
    // deadline ahead of us. Re-arm after our own wake even when its value is
    // unchanged; unrelated renders still do not reset this timer.
  }, [nextDeadline, wakeVersion]);
}
